import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, CheckCircle2, Clock, XCircle, Video, Building2 } from "lucide-react";

const cards = [
  { key: "today", label: "Today", Icon: Calendar, tone: "emerald" },
  { key: "upcoming", label: "Upcoming", Icon: Clock, tone: "blue" },
  { key: "completed", label: "Completed", Icon: CheckCircle2, tone: "slate" },
  { key: "cancelled", label: "Cancelled", Icon: XCircle, tone: "red" },
];

const tones = {
  emerald: "bg-emerald-50 text-emerald-700",
  blue: "bg-blue-50 text-blue-700",
  slate: "bg-slate-100 text-slate-700",
  red: "bg-red-50 text-red-700",
};

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [today, setToday] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, a] = await Promise.all([
        api.get("/doctor/stats"),
        api.get("/appointments"),
      ]);
      setStats(s.data);
      const todayIso = new Date().toISOString().slice(0, 10);
      setToday(a.data.filter((x) => x.date === todayIso && ["confirmed", "rescheduled"].includes(x.status)));
    })();
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm text-slate-500">Doctor dashboard</p>
        <h1 className="text-3xl font-bold text-slate-900">Welcome, {user?.name?.split(" ").slice(-1)[0] || "Doctor"}</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.key} className="doccare-card p-5">
            <div className={`w-9 h-9 rounded-xl grid place-items-center ${tones[c.tone]}`}><c.Icon className="w-4 h-4" /></div>
            <p className="mt-3 text-3xl font-bold text-slate-900" data-testid={`stat-${c.key}`}>{stats[c.key] ?? 0}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{c.label}</p>
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-3">Today's schedule</h2>
        {today.length === 0 ? (
          <div className="doccare-card p-8 text-center text-sm text-slate-500">No appointments scheduled today.</div>
        ) : (
          <div className="space-y-2">
            {today.sort((a, b) => a.start_time.localeCompare(b.start_time)).map((a) => (
              <div key={a.id} className="doccare-card p-4 flex items-center gap-4">
                <div className="text-center min-w-[64px]">
                  <p className="text-xs text-slate-400">TIME</p>
                  <p className="text-lg font-bold text-slate-900">{a.start_time}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">{a.patient.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                    {a.consultation_type === "video" ? <><Video className="w-3 h-3" /> Video</> : <><Building2 className="w-3 h-3" /> In-person</>}
                    <span>· #{a.id.slice(0, 8)}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
